'use client';
import React, { forwardRef } from 'react';

interface EditTextProps {
  placeholder?: string;
  value?: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  type?: 'text' | 'email' | 'password' | 'number' | 'tel' | 'url';
  disabled?: boolean;
  required?: boolean;
  className?: string;
  label?: string;
  error?: string;
  id?: string;
  name?: string;
  autoComplete?: string;
  maxLength?: number;
  minLength?: number;
  pattern?: string;
  readOnly?: boolean;
  size?: 'sm' | 'md' | 'lg';
  variant?: 'default' | 'filled' | 'outlined';
}

const EditText = forwardRef<HTMLInputElement, EditTextProps>(
  (
    {
      placeholder,
      value,
      onChange,
      type = 'text',
      disabled = false,
      required = false,
      className = '',
      label,
      error,
      id,
      name,
      autoComplete,
      maxLength,
      minLength,
      pattern,
      readOnly = false,
      size = 'md',
      variant = 'default',
      ...props
    },
    ref
  ) => {
    const sizes = {
      sm: 'px-3 py-2 text-sm',
      md: 'px-4 py-3 text-base',
      lg: 'px-5 py-4 text-lg',
    };

    const variants = {
      default: 'border border-text-muted bg-secondary focus:border-text-secondary-main',
      filled: 'border bg-secondary-light focus:border-text-secondary-main',
      outlined: 'border border-text-muted bg-transparent focus:border-text-secondary-main',
    };

    const baseClasses = `
    w-full
    rounded-lg
    font-inter
    transition-all
    duration-200
    ease-in-out
    focus:outline-none
    focus:ring-0
    focus:border-text-secondary-main
    placeholder:text-text-muted
    ${value ? 'border-text-secondary-main' : ''}
    ${disabled ? 'opacity-50 cursor-not-allowed bg-bg-light' : ''}
    ${error ? 'border-red-500 focus:border-red-500' : ''}
    ${readOnly ? 'bg-bg-light cursor-default' : ''}
  `
      .trim()
      .replace(/\s+/g, ' ');

    return (
      <div data-component-id="src/components/ui/EditText.tsx:82:6" data-component-path="src/components/ui/EditText.tsx" data-component-line="82" data-component-end-line="119" data-component-file="EditText.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22w-full%22%7D" className="w-full">
        {label && (
          <label data-component-id="src/components/ui/EditText.tsx:84:10" data-component-path="src/components/ui/EditText.tsx" data-component-line="84" data-component-end-line="87" data-component-file="EditText.tsx" data-component-name="label" data-component-content="%7B%22elementName%22%3A%22label%22%2C%22className%22%3A%22block%20text-sm%20font-medium%20text-text-primary%20mb-2%22%7D" htmlFor={id} className="block text-sm font-medium text-text-primary mb-2">
            {label}
            {required && <span data-component-id="src/components/ui/EditText.tsx:86:25" data-component-path="src/components/ui/EditText.tsx" data-component-line="86" data-component-end-line="86" data-component-file="EditText.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-red-500%20ml-1%22%2C%22textContent%22%3A%22*%22%7D" className="text-red-500 ml-1">*</span>}
          </label>
        )}
        <input data-component-id="src/components/ui/EditText.tsx:89:8" data-component-path="src/components/ui/EditText.tsx" data-component-line="89" data-component-end-line="113" data-component-file="EditText.tsx" data-component-name="input" data-component-content="%7B%22elementName%22%3A%22input%22%2C%22type%22%3A%22%5Bvar%3Atype%5D%22%2C%22id%22%3A%22%5Bvar%3Aid%5D%22%2C%22name%22%3A%22%5Bvar%3Aname%5D%22%2C%22value%22%3A%22%5Bvar%3Avalue%5D%22%2C%22%5Bspread%5D%22%3A%22true%22%7D"
          ref={ref}
          type={type}
          id={id}
          name={name}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          disabled={disabled}
          required={required}
          autoComplete={autoComplete}
          maxLength={maxLength}
          minLength={minLength}
          pattern={pattern}
          readOnly={readOnly}
          className={`
          ${baseClasses}
          ${sizes[size]}
          ${variants[variant]}
          ${className}
        `
            .trim()
            .replace(/\s+/g, ' ')}
          {...props}
        />
        {error && (
          <p data-component-id="src/components/ui/EditText.tsx:115:10" data-component-path="src/components/ui/EditText.tsx" data-component-line="115" data-component-end-line="117" data-component-file="EditText.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22mt-1%20text-sm%20text-red-500%22%7D" className="mt-1 text-sm text-red-500" role="alert">
            {error}
          </p>
        )}
      </div>
    );
  }
);

EditText.displayName = 'EditText';

export default EditText;
